import { useEffect, useState } from 'react';
import { createRoute, stripSearchParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { rootRoute, useAppRouterInitial } from '../routerBase';
import { getProduct, productsQueryKeys } from '../../api/products';
import {
  PRODUCTS_SEARCH_DEFAULTS,
  parseProductsSearch,
} from '../../hooks/useProductRouterSearch';
import { TableViewSkeleton } from '../../components/Skeleton/TableViewSkeleton';
import { DetailsSkeleton } from '../../components/Skeleton/DetailsSkeleton';
import { EditSkeleton } from '../../components/Skeleton/EditSkeleton';
import { useTranslation } from '../../hooks/useTranslation';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { CAPABILITIES } from '../../lib/capabilities';
import { requireCapability } from '../requireCapability';
import { EMPTY_CATEGORY_OPTIONS } from '../../components/Product/categories';
// Type-only imports so the per-route bundles stay split — the runtime modules
// are loaded via dynamic import() inside each route's effect (see below).
import type ProductManagement from '../../components/ProductManagement/index';
import type ProductDetails from '../../components/ProductDetails/index';
import type EditProduct from '../../components/EditProduct/index';
import type { ApiProduct } from '../../api/products';

export const PRODUCT_STALE_TIME_MS = 30_000;

// Shared query for /products/$id/{details,edit}. Mirrors `useResaleItemRaw`:
// the consumer hook `useProduct` applies a `select` mapper, but the route
// components feed the raw `ApiProduct` into their child components which do
// their own mapping. Both routes still share the cache via `productsQueryKeys.detail`.
export function useProductRaw(idNum: number, seed: ApiProduct | undefined) {
  return useQuery({
    queryKey: productsQueryKeys.detail(idNum),
    queryFn: () => getProduct(idNum),
    staleTime: PRODUCT_STALE_TIME_MS,
    select: (data) => data.product,
    ...(seed
      ? { initialData: { product: seed }, initialDataUpdatedAt: Date.now() }
      : {}),
  });
}

export const productsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products',
  validateSearch: parseProductsSearch,
  search: { middlewares: [stripSearchParams(PRODUCTS_SEARCH_DEFAULTS)] },
  component: function ProductsIndexRoute() {
    const { translations, currentUser, productCategories } = useAppRouterInitial();
    const { t } = useTranslation();
    useDocumentTitle(t('common.page_titles.products'));
    const [Component, setComponent] = useState<typeof ProductManagement | null>(null);
    useEffect(() => {
      import('../../components/ProductManagement/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <TableViewSkeleton label={t('common.loading')} />;
    }
    return (
      <Component
        translations={translations}
        currentUser={currentUser}
        categoryOptions={productCategories ?? EMPTY_CATEGORY_OPTIONS}
      />
    );
  },
});

export const productDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/$id/details',
  component: function ProductDetailsRoute() {
    const params = productDetailsRoute.useParams();
    const { initialProduct, currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const seed =
      initialProduct && initialProduct.id === idNum ? initialProduct : undefined;
    const { data } = useProductRaw(idNum, seed);
    useDocumentTitle(data?.name ?? t('common.page_titles.products'));
    const [Component, setComponent] = useState<typeof ProductDetails | null>(null);
    useEffect(() => {
      import('../../components/ProductDetails/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component || !data) {
      return <DetailsSkeleton label={t('common.loading')} />;
    }
    return <Component productId={idNum} initialProduct={data} currentUser={currentUser} />;
  },
});

export const productEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/$id/edit',
  // RBAC: edit gates on products.write end-to-end — ProductPolicy#edit?
  // requires write, so a direct hit to /products/:id/edit is rejected
  // server-side (redirect to root). This guard handles in-app navigation:
  // it redirects read-only users (no products.write) to the details page
  // before the component runs, client-side (replace), giving a friendlier
  // destination than the server's root redirect.
  beforeLoad: requireCapability(CAPABILITIES.PRODUCTS_WRITE, ({ params }) => ({
    to: '/products/$id/details',
    params: { id: params.id },
  })),
  component: function ProductEditRoute() {
    const params = productEditRoute.useParams();
    const { initialProduct, productCategories } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const seed =
      initialProduct && initialProduct.id === idNum ? initialProduct : undefined;
    const { data } = useProductRaw(idNum, seed);
    useDocumentTitle(
      data ? t('common.page_titles.edit', { name: data.name }) : t('common.page_titles.products'),
    );
    const [Component, setComponent] = useState<typeof EditProduct | null>(null);
    useEffect(() => {
      import('../../components/EditProduct/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component || !data) {
      return <EditSkeleton label={t('common.loading')} />;
    }
    return (
      <Component
        productId={idNum}
        initialProduct={data}
        categoryOptions={productCategories ?? EMPTY_CATEGORY_OPTIONS}
      />
    );
  },
});
